import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

export const Pagination = ({
  page = 1,
  totalPages = 1,
  onChange,
}) => {
  const theme = useTheme();

  if (totalPages < 1) return null;

  // Color primario acorde al tema de la aplicación
  const primaryColor =
    theme?.mode === "light"
      ? theme?.colors?.primary_dark || theme?.colors?.primary_light || "#1a4275"
      : theme?.colors?.primary_light || theme?.colors?.primary_dark || "#44BCF9";

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (page >= totalPages - 3) {
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  };

  const pages = getPageNumbers();

  const handlePrev = () => {
    if (page > 1 && onChange) {
      onChange(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages && onChange) {
      onChange(page + 1);
    }
  };

  const handlePageClick = (p) => {
    if (typeof p === "number" && p !== page && onChange) {
      onChange(p);
    }
  };

  return (
    <nav aria-label="Paginación" className="flex items-center justify-center gap-2 select-none py-2">
      {/* Botón Anterior */}
      <button
        type="button"
        onClick={handlePrev}
        disabled={page <= 1}
        aria-label="Página anterior"
        style={{
          width: "44px",
          height: "44px",
          minWidth: "44px",
          minHeight: "44px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
        className={`border transition-all duration-200 ${
          page <= 1
            ? "bg-[#f1f3f7] text-[#94a3b8] border-[#e2e8f0] cursor-not-allowed opacity-80"
            : "bg-white text-slate-700 border-[#e2e8f0] hover:bg-slate-50 shadow-sm cursor-pointer"
        }`}
      >
        <ChevronLeft size={18} />
      </button>

      {/* Números de página */}
      {pages.map((p, idx) => {
        if (p === "...") {
          return (
            <span
              key={`dots-${idx}`}
              style={{
                width: "44px",
                height: "44px",
                minWidth: "44px",
                minHeight: "44px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="text-slate-400 font-semibold"
            >
              ...
            </span>
          );
        }

        const isActive = p === page;

        return (
          <button
            key={p}
            type="button"
            onClick={() => handlePageClick(p)}
            aria-current={isActive ? "page" : undefined}
            style={{
              width: "44px",
              height: "44px",
              minWidth: "44px",
              minHeight: "44px",
              borderRadius: "12px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              ...(isActive
                ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#ffffff" }
                : { backgroundColor: "#ffffff", borderColor: "#e2e8f0", color: "#334155" }),
            }}
            className={`font-semibold text-base border transition-all duration-200 ${
              isActive
                ? "shadow-md"
                : "hover:bg-slate-50 shadow-sm cursor-pointer"
            }`}
          >
            {p}
          </button>
        );
      })}

      {/* Botón Siguiente */}
      <button
        type="button"
        onClick={handleNext}
        disabled={page >= totalPages}
        aria-label="Página siguiente"
        style={{
          width: "44px",
          height: "44px",
          minWidth: "44px",
          minHeight: "44px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
        className={`border transition-all duration-200 ${
          page >= totalPages
            ? "bg-[#f1f3f7] text-[#94a3b8] border-[#e2e8f0] cursor-not-allowed opacity-80"
            : "bg-white text-slate-700 border-[#e2e8f0] hover:bg-slate-50 shadow-sm cursor-pointer"
        }`}
      >
        <ChevronRight size={18} />
      </button>
    </nav>
  );
};

export default Pagination;
